export const currencyFormatter = (currency = '', locale = window.navigator.language) => {
  const options = currency
    ? { style: 'currency', currency }
    : {
        style: 'decimal',
        minimumFractionDigits: 2,
      };
  return new Intl.NumberFormat(locale, options);
};

// UBL PriceAmount pattern 0.00########: 2 to 10 fraction digits, half-even, no grouping.
const XML_UNIT_PRICE_SCALE = 10;

const expandScientific = (text) => {
  const match = /^(-?)(\d+)(?:\.(\d+))?e([+-]?\d+)$/i.exec(text);
  if (!match) {
    return text;
  }

  const sign = match[1];
  const whole = match[2];
  const fraction = match[3] || '';
  const exponent = Number(match[4]);
  const digits = `${whole}${fraction}`.replace(/^0+/, '') || '0';
  const point = whole.length + exponent;
  let plain;

  if (point <= 0) {
    plain = `0.${'0'.repeat(-point)}${digits}`;
  } else if (point >= digits.length) {
    plain = `${digits}${'0'.repeat(point - digits.length)}`;
  } else {
    plain = `${digits.slice(0, point)}.${digits.slice(point)}`;
  }

  return `${sign}${plain}`;
};

const withMinimumFractionDigits = (integer, fraction) => {
  const trimmed = fraction.replace(/0+$/, '');
  const shown = trimmed.length < 2 ? trimmed.padEnd(2, '0') : trimmed;

  return `${integer}.${shown}`;
};

const roundHalfEvenPlain = (plain, scale) => {
  const negative = plain.startsWith('-');
  const unsigned = negative ? plain.slice(1) : plain;
  const [rawInteger, rawFraction = ''] = unsigned.split('.');
  const integer = rawInteger.replace(/^0+(?=\d)/, '') || '0';

  if (rawFraction.length <= scale) {
    return `${negative ? '-' : ''}${withMinimumFractionDigits(integer, rawFraction)}`;
  }

  const kept = rawFraction.slice(0, scale);
  const nextDigit = Number(rawFraction[scale]);
  const hasFurtherDigits = /[1-9]/.test(rawFraction.slice(scale + 1));
  const lastKeptDigit = Number(kept[kept.length - 1] ?? integer[integer.length - 1]);
  const roundUp = nextDigit > 5 || (nextDigit === 5 && (hasFurtherDigits || lastKeptDigit % 2 !== 0));

  if (!roundUp) {
    return `${negative ? '-' : ''}${withMinimumFractionDigits(integer, kept)}`;
  }

  const scaled = BigInt(`${integer}${kept.padEnd(scale, '0')}`) + 1n;
  const scaledText = scaled.toString().padStart(scale + 1, '0');
  const roundedInteger = scaledText.slice(0, -scale).replace(/^0+(?=\d)/, '') || '0';
  const roundedFraction = scaledText.slice(-scale);

  return `${negative ? '-' : ''}${withMinimumFractionDigits(roundedInteger, roundedFraction)}`;
};

export const formatXmlUnitPrice = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return '0.00';
  }

  const plain = expandScientific(Math.abs(number).toString());
  const formatted = roundHalfEvenPlain(plain, XML_UNIT_PRICE_SCALE);

  if (number < 0 && formatted !== '0.00') {
    return `-${formatted}`;
  }

  return formatted;
};

// Same digits as formatXmlUnitPrice, with the locale grouping used by the 2-decimal summary rows.
export const formatGroupedExactAmount = (value, locale = window.navigator.language) => {
  const exact = formatXmlUnitPrice(value);
  const negative = exact.startsWith('-');
  const unsigned = negative ? exact.slice(1) : exact;
  const [integer, fraction = '00'] = unsigned.split('.');
  const parts = new Intl.NumberFormat(locale).formatToParts(1000.1);
  const group = parts.find((part) => part.type === 'group')?.value ?? ',';
  const decimal = parts.find((part) => part.type === 'decimal')?.value ?? '.';
  const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, group);

  return `${negative ? '-' : ''}${grouped}${decimal}${fraction}`;
};

// Keep numbers and +/- signs visually stable in RTL UIs.
export const NUMERIC_TEXT_SX = {
  direction: 'ltr',
  unicodeBidi: 'isolate',
  display: 'inline-block',
};
